import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  useMediaQuery,
  useTheme,
  Stack,
  Snackbar,
  InputAdornment,
  Skeleton,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';

import axiosInstance from '../../api/axiosConfig';

const Branches = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [branches, setBranches] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [showInactive, setShowInactive] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [openDialog, setOpenDialog] = useState(false);

  const [editingBranch, setEditingBranch] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    message: '',
  });

  const [formData, setFormData] = useState({
    branchName: '',
    directorName: '',
    branchHeadName: '',
    coordinatorName: '',
    primaryPhone: '',
    secondaryPhone: '',
    whatsAppNumber: '',
    address: '',
    email: '',
    googleMapUrl: '',
    displayOrder: 1,
    isActive: true,
  });

  // -----------------------------
  // Debounce Search
  // -----------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // -----------------------------
  // Initial Load
  // -----------------------------
  useEffect(() => {
    fetchBranches();
  }, [debouncedSearch, showInactive]);

  // -----------------------------
  // Toast Helper
  // -----------------------------
  const showToast = (message) => {
    setToast({
      open: true,
      message,
    });
  };

  // -----------------------------
  // Load Branches
  // -----------------------------
  const fetchBranches = async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get('/branch/search', {
        params: {
          search: debouncedSearch,
          isActive: !showInactive,
        },
      });

      const data = response.data || [];

      setBranches(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Failed to load branches');
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Open Dialog
  // -----------------------------
  const handleOpenDialog = async (branch = null) => {
    if (branch) {
      try {
        setDialogLoading(true);

        const response = await axiosInstance.get(
          `/branch/${branch.branchId}`
        );

        const b = response.data;

        setEditingBranch(b);

        setFormData({
          branchName: b.branchName || '',
          directorName: b.directorName || '',
          branchHeadName: b.branchHeadName || '',
          coordinatorName: b.coordinatorName || '',
          primaryPhone: b.primaryPhone || '',
          secondaryPhone: b.secondaryPhone || '',
          whatsAppNumber: b.whatsAppNumber || '',
          address: b.address || '',
          email: b.email || '',
          googleMapUrl: b.googleMapUrl || '',
          displayOrder: b.displayOrder || 1,
          isActive: b.isActive,
        });

        setOpenDialog(true);
      } catch {
        showToast('Failed to load branch details');
      } finally {
        setDialogLoading(false);
      }
    } else {
      setEditingBranch(null);

      setFormData({
        branchName: '',
        directorName: '',
        branchHeadName: '',
        coordinatorName: '',
        primaryPhone: '',
        secondaryPhone: '',
        whatsAppNumber: '',
        address: '',
        email: '',
        googleMapUrl: '',
        displayOrder: 1,
        isActive: true,
      });

      setOpenDialog(true);
    }
  };

  // -----------------------------
  // Save Branch
  // -----------------------------
  const handleSaveBranch = async () => {
    try {
      if (!formData.branchName.trim()) {
        showToast('Branch Name is required');
        return;
      }

      if (!formData.primaryPhone.trim()) {
        showToast('Primary Phone is required');
        return;
      }

      if (!formData.address.trim()) {
        showToast('Address is required');
        return;
      }

      if (editingBranch) {
        await axiosInstance.put(
          `/branch/${editingBranch.branchId}`,
          formData
        );

        showToast('Branch updated successfully');
      } else {
        await axiosInstance.post('/branch', formData);

        showToast('Branch created successfully');
      }

      setOpenDialog(false);
      fetchBranches();
    } catch (err) {
      console.error(err);
      showToast('Failed to save branch');
    }
  };

  // -----------------------------
  // Toggle Active / Inactive
  // -----------------------------
  const handleToggleStatus = async (branchId) => {
    try {
      await axiosInstance.patch(
        `/branch/${branchId}/status`
      );

      showToast('Status updated');

      fetchBranches();
    } catch {
      showToast('Failed to update status');
    }
  };

  // -----------------------------
  // Form Change
  // -----------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const paginatedBranches = branches.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const totalPages = Math.ceil(
    branches.length / pageSize
  );

    return (
    <Box>

      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Branch Management
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Branch
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search + Filters */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: 2,
        }}
      >
        <CardContent>
          <Grid container spacing={2}>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Branch"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),

                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setSearchTerm('')
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) =>
                      setShowInactive(
                        e.target.checked
                      )
                    }
                  />
                }
                label={
                  showInactive
                    ? 'Showing Inactive Branches'
                    : 'Showing Active Branches'
                }
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Branch Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Table>

          <TableHead
            sx={{
              bgcolor: 'primary.main',
            }}
          >
            <TableRow>

              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Branch Name
              </TableCell>

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Director
                </TableCell>
              )}

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Phone
                </TableCell>
              )}

              {!isMobile && (
                <TableCell
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                  }}
                >
                  Order
                </TableCell>
              )}

              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Status
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                }}
              >
                Actions
              </TableCell>

            </TableRow>
          </TableHead>

          <TableBody>

            {/* Skeleton Loader */}
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton />
                  </TableCell>

                  <TableCell>
                    <Skeleton />
                  </TableCell>

                  <TableCell>
                    <Skeleton />
                  </TableCell>

                  <TableCell>
                    <Skeleton />
                  </TableCell>

                  <TableCell>
                    <Skeleton />
                  </TableCell>

                  <TableCell>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ))
            ) : paginatedBranches.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  align="center"
                >
                  No branches found
                </TableCell>
              </TableRow>
            ) : (
              paginatedBranches.map(
                (branch) => (
                  <TableRow
                    key={branch.branchId}
                    hover
                  >

                    <TableCell>
                      <Typography
                        fontWeight={600}
                      >
                        {
                          branch.branchName
                        }
                      </Typography>
                    </TableCell>

                    {!isMobile && (
                      <TableCell>
                        {branch.directorName ||
                          '-'}
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell>
                        {
                          branch.primaryPhone
                        }
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell>
                        {
                          branch.displayOrder
                        }
                      </TableCell>
                    )}

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          branch.isActive
                            ? 'Active'
                            : 'Inactive'
                        }
                        color={
                          branch.isActive
                            ? 'success'
                            : 'error'
                        }
                      />
                    </TableCell>

                    <TableCell align="center">

                      <IconButton
                        color="primary"
                        onClick={() =>
                          handleOpenDialog(
                            branch
                          )
                        }
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        color={
                          branch.isActive
                            ? 'warning'
                            : 'success'
                        }
                        onClick={() =>
                          handleToggleStatus(
                            branch.branchId
                          )
                        }
                      >
                        {branch.isActive ? (
                          <BlockIcon />
                        ) : (
                          <CheckCircleIcon />
                        )}
                      </IconButton>

                    </TableCell>

                  </TableRow>
                )
              )
            )}

          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={3}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) =>
              setPage(value)
            }
            color="primary"
          />
        </Box>
      )}
      {/* Add / Edit Branch Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingBranch ? 'Edit Branch' : 'Add Branch'}
        </DialogTitle>

        <DialogContent dividers>
          {dialogLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              py={4}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} mt={0.5}>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Branch Name"
                  name="branchName"
                  value={formData.branchName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Director Name"
                  name="directorName"
                  value={formData.directorName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Branch Head Name"
                  name="branchHeadName"
                  value={formData.branchHeadName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Coordinator Name"
                  name="coordinatorName"
                  value={formData.coordinatorName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Primary Phone"
                  name="primaryPhone"
                  value={formData.primaryPhone}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Secondary Phone"
                  name="secondaryPhone"
                  value={formData.secondaryPhone}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  name="whatsAppNumber"
                  value={formData.whatsAppNumber}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Google Map URL"
                  name="googleMapUrl"
                  value={formData.googleMapUrl}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Display Order"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          isActive: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Active Branch"
                />
              </Grid>

            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveBranch}
          >
            {editingBranch
              ? 'Update Branch'
              : 'Save Branch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Message */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() =>
          setToast({
            open: false,
            message: '',
          })
        }
        message={toast.message}
      />

    </Box>
  );
};

export default Branches;
